import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box, Tabs, Tab, Typography, Stack, Button, Tooltip,
  Paper, IconButton, CircularProgress,
} from '@mui/material'
import {
  Inventory as InventoryIcon,
  History as HistoryIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  AddCircleOutline as AdjustIcon,
  ViewList as ListIcon,
} from '@mui/icons-material'
import InventoryMUI from './InventoryMUI'
import BulkStockAdjustmentPage from './BulkStockAdjustmentPage'
import StockMovementHistory from './StockMovementHistory'
import StockAdjustmentsEnhanced from './StockAdjustmentsEnhanced'
import { useGetInventoryDashboardQuery, useGetLowStockVariantsQuery } from '../store/services/inventoryApi'
import { formatCompactCurrency } from '../utils/currency'
import api from '../services/api'

const TAB_KEYS = ['stock', 'bulk', 'movements', 'adjustments']

const TABS = [
  { key: 'stock',       label: 'Stock Levels',    icon: <ListIcon fontSize="small" /> },
  { key: 'bulk',        label: 'Bulk Adjust',     icon: <EditIcon fontSize="small" /> },
  { key: 'movements',   label: 'Movements',       icon: <HistoryIcon fontSize="small" /> },
  { key: 'adjustments', label: 'Adj. Log',        icon: <AssessmentIcon fontSize="small" /> },
]

function KpiCard({ label, value, color = 'text.primary', bg = 'background.paper' }: {
  label: string; value: React.ReactNode; color?: string; bg?: string
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: 2, py: 1.2, borderRadius: 2, flex: 1, minWidth: 100,
        backgroundColor: bg,
        borderColor: 'divider',
      }}
    >
      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} color={color} sx={{ lineHeight: 1.4 }}>
        {value}
      </Typography>
    </Paper>
  )
}

interface InventoryManagementTabsProps {
  defaultTab?: string
}

const InventoryManagementTabs: React.FC<InventoryManagementTabsProps> = ({ defaultTab }) => {
  const location = useLocation()
  const navigate = useNavigate()

  const getTabFromUrl = () => {
    const params = new URLSearchParams(location.search)
    const t = params.get('tab')
    const idx = TAB_KEYS.indexOf(t || '')
    return idx >= 0 ? idx : TAB_KEYS.indexOf(defaultTab || 'stock') >= 0 ? TAB_KEYS.indexOf(defaultTab!) : 0
  }

  const [activeTab, setActiveTab] = useState(getTabFromUrl)
  const [warehouseCount, setWarehouseCount] = useState<number | null>(null)

  const { data: dashboard, isLoading: dashLoading, refetch: refetchDash } = useGetInventoryDashboardQuery()
  const { data: lowStock = [] } = useGetLowStockVariantsQuery({ threshold_multiplier: 1.0, limit: 50 })

  useEffect(() => {
    setActiveTab(getTabFromUrl())
  }, [location.search])

  useEffect(() => {
    api.get('/admin/inventory/warehouses').then(r => setWarehouseCount((r.data || []).length)).catch(() => {})
  }, [])

  const handleTabChange = (_: React.SyntheticEvent, val: number) => {
    setActiveTab(val)
    const params = new URLSearchParams(location.search)
    params.set('tab', TAB_KEYS[val])
    navigate(`${location.pathname}?${params.toString()}`, { replace: true })
  }

  const s = dashboard?.summary

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ── Compact header strip ── */}
      <Box sx={{ px: 3, pt: 2, pb: 0, backgroundColor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <InventoryIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>Inventory</Typography>
            {dashLoading && <CircularProgress size={14} />}
          </Stack>

          {/* Quick actions */}
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh dashboard">
              <IconButton size="small" onClick={() => refetchDash()}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AdjustIcon fontSize="small" />}
              onClick={() => handleTabChange({} as any, 1)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Bulk Adjust
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<HistoryIcon fontSize="small" />}
              onClick={() => handleTabChange({} as any, 2)}
              sx={{ textTransform: 'none' }}
            >
              Movements
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<WarningIcon fontSize="small" />}
              color={lowStock.length > 0 ? 'warning' : 'primary'}
              onClick={() => handleTabChange({} as any, 0)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {lowStock.length > 0 ? `${lowStock.length} Low Stock` : 'Alerts'}
            </Button>
          </Stack>
        </Box>

        {/* ── KPI row ── */}
        {s && (
          <Stack direction="row" spacing={1.5} sx={{ mb: 1.5, flexWrap: 'wrap' }} useFlexGap>
            <KpiCard label="Variants" value={s.total_variants.toLocaleString()} />
            <KpiCard label="Stock Qty" value={s.total_stock_quantity.toLocaleString()} />
            <KpiCard label="Stock Value" value={formatCompactCurrency(s.total_stock_value)} color="success.main" />
            <KpiCard
              label="Low Stock"
              value={s.low_stock_variants}
              color={s.low_stock_variants > 0 ? 'warning.main' : 'text.primary'}
              bg={s.low_stock_variants > 0 ? 'warning.50' : 'background.paper'}
            />
            <KpiCard
              label="Out of Stock"
              value={s.out_of_stock_variants}
              color={s.out_of_stock_variants > 0 ? 'error.main' : 'text.primary'}
            />
            <KpiCard label="Overstocked" value={s.overstocked_variants} color="info.main" />
            {warehouseCount !== null && (
              <KpiCard label="Warehouses" value={warehouseCount} />
            )}
          </Stack>
        )}

        {/* ── Tab bar ── */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 500, fontSize: '0.85rem', py: 0.5 },
            '& .Mui-selected': { fontWeight: 700 },
          }}
        >
          {TABS.map(t => (
            <Tab key={t.key} icon={t.icon} iconPosition="start" label={t.label} />
          ))}
        </Tabs>
      </Box>

      {/* ── Tab panels ── */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {activeTab === 0 && <InventoryMUI compact />}
        {activeTab === 1 && <BulkStockAdjustmentPage />}
        {activeTab === 2 && <StockMovementHistory />}
        {activeTab === 3 && <StockAdjustmentsEnhanced />}
      </Box>
    </Box>
  )
}

export default InventoryManagementTabs
